import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, CheckCircle2, AlertTriangle, SearchX } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OtterAssistSetupFields, serializeAssist, setupValid } from "@/components/social/OtterAssistComposerPanel";

// Otter Assist V1 Phase 5 — the post-submission activation experience. Matching is asynchronous
// (OAD-052): the Chatter post is already published and visible; this dialog polls the member-safe
// COUNT-ONLY preview endpoint with bounded exponential backoff and never shows an identity, a
// score, a band, evidence or any internal scoring data.

const FIRST_DELAY = 1500;
const MAX_DELAY = 15000;
const MAX_POLLS = 40;

const TERMINAL = ["active", "no_matches", "matching_failed", "expired", "closed", "help_received",
  "awaiting_confirmation"];

const SUGGESTION_COPY = {
  change_required_filter_to_preferred: "Make a Required filter Preferred instead",
  expand_audience_scope: "Open it up to the wider eligible community",
  broaden_region: "Widen or remove the region",
  clarify_need_summary: "Add a little more detail about what you need",
};
const suggestionText = (s) =>
  (typeof s === "string" ? SUGGESTION_COPY[s] || s.replace(/_/g, " ") : s.label || s.suggestion);

export function OtterAssistActivationDialog({ info, setup, config, industryOpts, identityType,
                                              onClose }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(setup);
  const [polls, setPolls] = useState(0);
  const [error, setError] = useState(null);
  const timer = useRef(null);
  const cancelled = useRef(false);

  const assistId = info?.assist_id;

  const poll = useCallback(async (delay, count) => {
    if (cancelled.current || !assistId) return;
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/otter-assist/assists/${assistId}/preview`);
        if (cancelled.current) return;
        setStatus(data);
        setPolls(count);
        if (TERMINAL.includes(data.assist_status) || count >= MAX_POLLS) return;
        poll(Math.min(Math.round(delay * 1.5), MAX_DELAY), count + 1);
      } catch (e) {
        if (cancelled.current) return;
        if (e?.response?.status === 429) {
          setError("Checking too often — pausing for a moment.");
          poll(MAX_DELAY, count + 1);
          return;
        }
        setError("We couldn't check the matching status. Your post is published.");
      }
    }, delay);
  }, [assistId]);

  useEffect(() => {
    cancelled.current = false;
    if (assistId) poll(info?.poll_after_ms || FIRST_DELAY, 1);
    return () => { cancelled.current = true; if (timer.current) clearTimeout(timer.current); };
  }, [assistId, info, poll]);

  const restartPolling = () => { setStatus((s) => ({ ...(s || {}), assist_status: "matching" })); poll(FIRST_DELAY, 1); };

  const act = async (fn, successMessage) => {
    setBusy(true);
    try {
      await fn();
      if (successMessage) toast.success(successMessage);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work — please try again.");
    } finally { setBusy(false); }
  };

  const continueNow = () => act(async () => {
    const { data } = await api.post(`/otter-assist/assists/${assistId}/confirm`, { confirm: true });
    setStatus((s) => ({ ...(s || {}), assist_status: data.status || "active", charged: true }));
  }, "Otter Assist is active");

  const rerun = (body) => act(async () => {
    await api.post(`/otter-assist/assists/${assistId}/rerun`, body || {});
    setEditing(false);
    restartPolling();
  });

  const continueWithout = () => act(async () => {
    // OAD-063 — an explicit sender choice: keep the Chatter post, remove the public Otter Assist
    // presentation entirely. Never triggered by the match count.
    await api.post(`/otter-assist/assists/${assistId}/status-action`,
      { action: "continue_without", confirm: true });
    toast.success("Otter Assist removed — your post stays published and no Assist was used");
    onClose(true);
  });

  if (!assistId) return null;
  const state = status?.assist_status || "matching";
  const count = status?.strong_match_count ?? 0;
  const suggestions = status?.broadening_suggestions || [];

  const header = {
    matching: { icon: Loader2, title: "Finding people who can help", spin: true },
    active: { icon: CheckCircle2, title: "Otter Assist is active" },
    awaiting_confirmation: { icon: Sparkles, title: `${count} ${count === 1 ? "match" : "matches"} found` },
    no_matches: { icon: SearchX, title: "No matches yet" },
    matching_failed: { icon: AlertTriangle, title: "Matching couldn't finish" },
    expired: { icon: AlertTriangle, title: "This Otter Assist expired" },
    closed: { icon: CheckCircle2, title: "Otter Assist removed" },
  }[state] || { icon: Sparkles, title: "Otter Assist" };
  const Icon = header.icon;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(false); }}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid="otter-assist-activation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Icon className={`h-4 w-4 text-teal-700 ${header.spin ? "animate-spin" : ""}`} />
            <span data-testid="otter-assist-activation-title">{header.title}</span>
          </DialogTitle>
          <DialogDescription data-testid="otter-assist-activation-state">
            Your update is already published on Chatter.
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-xs text-amber-800" data-testid="otter-assist-activation-error">{error}</p>}

        {state === "matching" && (
          <p className="text-sm text-muted-foreground" data-testid="otter-assist-matching-copy">
            Otter Assist is reading your request and looking for people who can genuinely help.
            You can close this — we'll keep going in the background.
            {polls >= MAX_POLLS ? " This is taking longer than usual; check My Needs shortly." : ""}
          </p>
        )}

        {state === "active" && (
          <div className="space-y-2" data-testid="otter-assist-active-result">
            <p className="text-sm text-foreground">
              {count} {count === 1 ? "match" : "matches"} found. One Otter Assist was used.
            </p>
            <p className="text-xs text-muted-foreground">
              We never show you who was matched until someone chooses to offer help.
            </p>
          </div>
        )}

        {state === "awaiting_confirmation" && !editing && (
          <div className="space-y-3" data-testid="otter-assist-low-match">
            <p className="text-sm text-foreground">
              Otter Assist found <strong data-testid="otter-assist-low-match-count">{count}</strong>{" "}
              {count === 1 ? "match" : "matches"} — fewer than five. Nothing has been used yet.
              Choose how you'd like to continue.
            </p>
            {suggestions.length > 0 && (
              <div className="rounded-md bg-secondary/60 p-2.5" data-testid="otter-assist-broadening">
                <div className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground">Ideas that often help</div>
                <ul className="mt-1 list-inside list-disc text-[11px] text-muted-foreground">
                  {suggestions.map((s, i) => <li key={i}>{suggestionText(s)}</li>)}
                </ul>
                <p className="mt-1 text-[11px] text-muted-foreground">Suggestions only — nothing changes unless you edit it.</p>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <Button type="button" disabled={busy} onClick={continueNow}
                data-testid="otter-assist-continue-with-matches">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Continue with {count} {count === 1 ? "match" : "matches"} (uses 1 Assist)
              </Button>
              <Button type="button" variant="outline" disabled={busy} onClick={() => setEditing(true)}
                data-testid="otter-assist-edit-and-rerun">Edit and run matching again (free)</Button>
              <Button type="button" variant="ghost" disabled={busy} onClick={continueWithout}
                data-testid="otter-assist-continue-without">Continue without Otter Assist</Button>
            </div>
          </div>
        )}

        {state === "no_matches" && !editing && (
          <div className="space-y-3" data-testid="otter-assist-no-match">
            <p className="text-sm text-foreground">
              Otter Assist couldn't find anyone who clearly matches this request right now. Nothing
              was used, and your post is live as normal.
            </p>
            {suggestions.length > 0 && (
              <div className="rounded-md bg-secondary/60 p-2.5" data-testid="otter-assist-broadening">
                <ul className="list-inside list-disc text-[11px] text-muted-foreground">
                  {suggestions.map((s, i) => <li key={i}>{suggestionText(s)}</li>)}
                </ul>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <Button type="button" variant="outline" disabled={busy} onClick={() => setEditing(true)}
                data-testid="otter-assist-edit-and-rerun">Edit and run matching again (free)</Button>
              <Button type="button" variant="ghost" disabled={busy} onClick={continueWithout}
                data-testid="otter-assist-continue-without">Continue without Otter Assist</Button>
            </div>
          </div>
        )}

        {state === "matching_failed" && !editing && (
          <div className="space-y-3" data-testid="otter-assist-failed">
            <p className="text-sm text-foreground">
              Something went wrong on our side while matching. This isn't about your request, and
              nothing was used. Your post and your Otter Assist setup are both saved.
            </p>
            <div className="flex flex-col gap-2">
              <Button type="button" disabled={busy} onClick={() => rerun({})}
                data-testid="otter-assist-retry">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Try matching again
              </Button>
              <Button type="button" variant="ghost" disabled={busy} onClick={continueWithout}
                data-testid="otter-assist-continue-without">Continue without Otter Assist</Button>
            </div>
          </div>
        )}

        {state === "expired" && (
          <p className="text-sm text-foreground" data-testid="otter-assist-expired">
            The deadline passed before this Otter Assist could go out. Nothing was used. You can
            start a new one from a fresh post.
          </p>
        )}

        {editing && (
          <div className="space-y-3" data-testid="otter-assist-rerun-editor">
            <OtterAssistSetupFields config={config} setup={draft} setSetup={setDraft}
              identityType={identityType} industryOpts={industryOpts} idSuffix="-rerun" />
            <p className="text-[11px] text-muted-foreground">
              Running matching again is free — you'll never be charged twice for the same Assist.
            </p>
            <div className="flex gap-2">
              <Button type="button" size="sm" disabled={busy || !setupValid(draft, config)}
                onClick={() => rerun(serializeAssist(draft))} data-testid="otter-assist-rerun-submit">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Run matching again
              </Button>
              <Button type="button" size="sm" variant="ghost" disabled={busy}
                onClick={() => setEditing(false)} data-testid="otter-assist-rerun-cancel">Back</Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onClose(false)}
            data-testid="otter-assist-activation-close">
            {state === "matching" ? "Close and keep going" : "Done"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OtterAssistActivationDialog;
